'use client'

import { useEffect, useState } from 'react'
import styles from './legal.module.css'

/* =========================================
   LEGAL TABLE OF CONTENTS
   In-page navigation for the legal documents, derived from the
   pages' h2 section headings (each page passes its own id/label
   list and stamps matching ids on the headings).

   Desktop: a sticky rail beside the article with a scrollspy
   highlight. The highlight is computed on scroll (last heading
   above the reading line wins) rather than IntersectionObserver:
   legal sections vary from a few lines to several screens, and
   the scroll rule stays correct for both.

   Mobile: the rail hides and a native <details> disclosure
   renders instead, so collapsing costs no JS at all.
   ========================================= */

export type LegalSection = {
  id: string
  label: string
}

/* Must clear the sticky .legal-nav plus the headings'
   scroll-margin-top so the highlighted entry matches the section
   the reader actually landed on after an anchor jump. */
const READING_LINE_PX = 112

export function LegalToc({ sections }: { sections: LegalSection[] }) {
  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    let frame = 0

    const update = () => {
      frame = 0
      let current: string | null = null
      for (const section of sections) {
        const heading = document.getElementById(section.id)
        if (!heading) continue
        if (heading.getBoundingClientRect().top <= READING_LINE_PX) {
          current = section.id
        } else {
          break
        }
      }
      setActiveId(current)
    }

    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [sections])

  const renderLinks = (listClass: string) => (
    <ol className={listClass}>
      {sections.map((section, index) => (
        <li key={section.id}>
          <a
            href={`#${section.id}`}
            className={
              activeId === section.id
                ? `${styles.tocLink} ${styles.tocActive}`
                : styles.tocLink
            }
            aria-current={activeId === section.id ? 'true' : undefined}
          >
            <span className={styles.tocNum} aria-hidden="true">
              {`[${index + 1}]`}
            </span>
            {section.label}
          </a>
        </li>
      ))}
    </ol>
  )

  return (
    <div className={styles.tocZone}>
      <nav className={styles.toc} aria-label="Table of contents">
        <p className={styles.tocLabel}>Contents</p>
        {renderLinks(styles.tocList)}
      </nav>
      <details className={styles.tocMobile}>
        <summary className={styles.tocSummary}>Contents</summary>
        {renderLinks(styles.tocMobileList)}
      </details>
    </div>
  )
}
